import { useEffect, useState } from 'react';

/**
 * True once `active` has been continuously true for `delayMs`; false the moment
 * it goes false again. Use to gate a loading placeholder so it only appears for
 * work slow enough to be worth mentioning — a spinner that shows and vanishes
 * inside a few hundred milliseconds is noise, but showing nothing at all makes
 * "still loading" indistinguishable from "nothing to show".
 *
 * Deliberately NOT `useDebouncedValue`. That hook seeds its state with the
 * value it is given, so on mount it returns the live value immediately — for a
 * flag that starts true (a query's `isLoading`) it would fire straight away and
 * there would be no delay at all.
 */
export function useDelayedFlag(active: boolean, delayMs: number): boolean {
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    if (!active) {
      setElapsed(false);
      return;
    }
    const timer = setTimeout(() => setElapsed(true), delayMs);
    return () => clearTimeout(timer);
  }, [active, delayMs]);
  return elapsed;
}
